import { Form, Button, Row } from 'react-bootstrap';
import { Formik, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { CURRENCIES, TIMEZONES } from '../../constants';

import { WithConfirmation } from '../common';

const GeneralForm = ({ handleUpdate, isSubmitting, userInfo }) => {
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(
    userInfo.timezone || Object.keys(timezones)[0]
  );
  const permissions = useSelector((reducer) => reducer.permissionsReducer);
  useEffect(() => {
    setTimezones(TIMEZONES);
  }, []);

  const TimeZone = ({ handleShow }) => {
    const { values, setFieldValue, setFieldTouched } = useFormikContext();

    const selectOptions = timezones;

    const selectedOption = selectOptions.find(
      (opt) => opt.value === values.timezone
    );

    const handleChange = (selected) => {
      const value = selected?.value || '';
      setFieldValue('timezone', value);
      setFieldTouched('timezone', true);

      if (value) {
        handleShow?.();
        setSelectedTimezone(value);
      }
    };

    return (
      <Select
        name="timezone"
        options={selectOptions}
        value={selectedOption || null}
        onChange={handleChange}
        classNamePrefix="react-select"
      />
    );
  };

  TimeZone.propTypes = {
    handleShow: PropTypes.func,
  };

  // HOC - Confirmation modal
  const TimeZoneChangeWithConfirmation = WithConfirmation(TimeZone);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        emailAddress: userInfo.email,
        phone: userInfo.phone || '',
        timezone: userInfo.timezone || Object.keys(timezones)[0],
        currency: userInfo.currency || 'USD',
      }}
      validationSchema={Yup.object({
        firstName: Yup.string().required('Please enter your name'),
        emailAddress: Yup.string()
          .trim()
          .email('Please enter a valid email address')
          .required('Please enter your email address'),
        phone: Yup.string()
          .matches(/^[0-9 -]+$|^$|^\s$/, {
            message: 'Please enter valid phone number.',
            excludeEmptyString: true,
          })
          .max(16, 'Max digits allowed is 16')
          .nullable(),
      })}
      onSubmit={(values) => {
        handleUpdate(values);
      }}
    >
      {(formikprops) => (
        <Form onSubmit={formikprops.handleSubmit} className="needs-validation">
          <hr className="my-3" />
          <Row>
            {userInfo.lastName !== null && userInfo.lastName.length !== 0 ? (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <Form.Label className="form-control-label">
                    First name
                  </Form.Label>
                  <Field
                    name="firstName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="firstName"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </div>
              </div>
            ) : (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <Form.Label className="form-control-label">
                    Full Name
                  </Form.Label>
                  <Field
                    name="firstName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="firstName"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </div>
              </div>
            )}
            {userInfo.lastName !== null && userInfo.lastName.length !== 0 && (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <Form.Label className="form-control-label">
                    Last name
                  </Form.Label>
                  <Field
                    name="lastName"
                    type="text"
                    className="form-control"
                    maxLength="19"
                  />
                  <ErrorMessage
                    name="lastName"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </div>
              </div>
            )}
            <div className="col-12 col-md-6">
              <div className="form-group">
                <Form.Label className="form-control-label">
                  Email address
                </Form.Label>
                <Field
                  name="emailAddress"
                  type="text"
                  className="form-control"
                />
                <ErrorMessage
                  name="emailAddress"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-group">
                <Form.Label className="form-control-label">Phone</Form.Label>
                <Field
                  name="phone"
                  type="text"
                  className="form-control"
                  maxLength="16"
                />
                <ErrorMessage
                  name="phone"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-group">
                <Form.Label className="form-control-label">Timezone</Form.Label>
                <TimeZoneChangeWithConfirmation
                  modalProps={{
                    title: 'Timezone Change?',
                    action: () =>
                      formikprops.setFieldValue('timezone', selectedTimezone),
                    content: [
                      'Please note that any schedules created prior to this change will remain in the old timezone and will not automatically update to the new timezone.',
                      '',
                      'Are you sure you want to proceed with this change?',
                    ],
                    actionBtnText: 'Proceed',
                  }}
                />
              </div>
            </div>
          </Row>
          <Row>
            {permissions && permissions.includes('subscription_manage') && (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <Form.Label className="form-control-label">
                    Currency
                  </Form.Label>
                  <Field as="select" name="currency" className="form-select">
                    {Object.keys(CURRENCIES).map((e, i) => (
                      <option value={e} key={i}>
                        {CURRENCIES[e]}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>
            )}
          </Row>

          <div className="text-end mb-5">
            <Button
              className="btn btn-primary"
              type="submit"
              disabled={
                !(formikprops.isValid && formikprops.dirty) || isSubmitting
              }
            >
              Save changes
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

GeneralForm.propTypes = {
  isSubmitting: PropTypes.bool,
  handleUpdate: PropTypes.func,
  userInfo: PropTypes.object,
};

export default GeneralForm;
